import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from '@godaddy/react-mintl';
import PropTypes from 'prop-types';
import Button from '@ux/button';
import Modal from '@ux/modal';

import { publish, useSubscribe } from '../lib/usePubSub';
import useConfig from '../lib/useConfig';
import { useModalOpen } from '../lib/useModalOpen';
import rejectingFetch from '../lib/rejectingFetch';
import { addGrowlMessage } from '../lib/ConditionalGrowl';

import '@ux/modal/styles';
import './OptOutModal.css';

const ENABLE_QUALTRICS_SURVEY = true;
// eslint-disable-next-line id-length
export const QUALTRICS_SURVEY_FAILURE_TIMEOUT = 5000;

export const OptOutModal = ({ onClose, appEid }) => {
  useModalOpen();
  const [sending, setSending] = useState(false);
  const { formatMessage } = useIntl();
  const pageEid = appEid.extend('opt_out_modal');

  const {
    optinApi,
    optOutRedirect,
    proApi
  } = useConfig();

  const submitOptOut = useCallback(async () => {
    setSending(true);

    try {
      await rejectingFetch(`${optinApi}/optin`, {
        mode: 'cors',
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pcx: false
        })
      });

      window.redirectAfterOptout = () => { window.location.href = optOutRedirect; };

      if (!ENABLE_QUALTRICS_SURVEY) {
        window.redirectAfterOptout();
        return;
      }

      try {
        // Show the Survey
        window.qualtricsParams = { hubOptOut: true };
        window.QSI.API.unload();
        window.QSI.API.load();
        // fallback in case Qualtrics survey fails to load for some reason
        setTimeout(() => {
          if (window.document.getElementsByClassName('QSISlider').length === 0) {
            window.redirectAfterOptout();
          }
        }, QUALTRICS_SURVEY_FAILURE_TIMEOUT);
      } catch (e) {
        // in case QSI failed to load...
        window.redirectAfterOptout();
      }
    } catch (e) {
      setSending(false);
      addGrowlMessage({
        title: formatMessage({ id: 'OPT_OUT_MODAL_FAILURE_TITLE' }),
        content: formatMessage({ id: 'OPT_OUT_MODAL_FAILURE_CONTENT' }),
        icon: 'none',
        fadeTime: 6000
      });
    }
  }, [optinApi, optOutRedirect, proApi]);

  return (
    <Modal
      title={ formatMessage({ id: 'OPT_OUT_MODAL_TITLE' }) }
      id='opt-out-modal'
      onClose={ onClose }
      className='ux-modal'
    >
      <p className='opt-out-modal-content'>
        <FormattedMessage id='OPT_OUT_MODAL_CHOICE_BODY'
          values={{
            newline: <div className='mb-3' />
          }}
        />
      </p>
      <div className='opt-out-modal-actions'>
        <Button
          design='primary'
          disabled={ sending }
          type='submit'
          onClick={ () => submitOptOut() }
          data-eid={ pageEid('opt_out_modal_leave_hub.button.click') }
          text={ formatMessage({ id: 'OPT_OUT_MODAL_OPT_OUT_HUB' }) } />
        <Button
          design='secondary'
          disabled={ sending }
          onClick={ onClose }
          data-eid={ pageEid('opt_out_modal_cancel.button.click') }
          text={ formatMessage({ id: 'OPT_OUT_MODAL_NO' }) } />
      </div>
    </Modal>
  );
};

OptOutModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  appEid: PropTypes.func
};

const OPEN_OPT_OUT_MODAL_TOPIC = 'OPEN_OPT_OUT_MODAL';

export const OptOutModalContainer = ({ appEid }) => {
  const [optOutOpen, setOptOutOpen] = useState(false);

  const onCloseModal = useCallback((event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    setOptOutOpen(false);
  }, []);

  const openModal = useCallback(() => {
    setOptOutOpen(true);
  }, []);

  useSubscribe(openModal, OPEN_OPT_OUT_MODAL_TOPIC);

  if (!optOutOpen) {
    return null;
  }

  return (
    <OptOutModal onClose={ onCloseModal } appEid={ appEid } />
  );
};

OptOutModalContainer.propTypes = {
  appEid: PropTypes.func
};

export const openOptOutModal = () => publish(OPEN_OPT_OUT_MODAL_TOPIC);
